import { useEffect, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { InputSearchSelect } from '@/design_system/InputSearchSelect/InputSearchSelect';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import {
  useWorkshopWarrantyReturnRequests,
  type WorkshopWarrantyReturnRequest,
} from '@/models/article';
import useDebouncedState from '@/utils/useDebouncedState';

export const OriginalRequestSearchSelect = ({
  articleId,
  defect,
  originalRequestId,
  onChange,
  isDisabled,
}: {
  articleId: string;
  defect: {
    id: string;
    exceptionalBrandResponsibilityCoverageOriginalRequest: WorkshopWarrantyReturnRequest | null;
  };
  originalRequestId: string | null;
  onChange: (option: WorkshopWarrantyReturnRequest | null) => void;
  isDisabled?: boolean;
}) => {
  const { t } = useLingui();

  const [selectedOption, setSelectedOption] = useState<WorkshopWarrantyReturnRequest | null>(
    defect.exceptionalBrandResponsibilityCoverageOriginalRequest
  );
  const [search, debouncedSearch, setSearch] = useDebouncedState('', 500);

  const { data: { requests } = { requests: [] }, isFetching } = useWorkshopWarrantyReturnRequests(
    {
      articleId,
      defectId: defect.id,
      search: debouncedSearch || undefined,
      limit: 10,
    },
    {
      keepPreviousData: false,
      enabled: !isDisabled && !!debouncedSearch,
    }
  );

  useEffect(() => {
    if (!originalRequestId) {
      setSelectedOption(null);

      return;
    }

    if (selectedOption?.id === originalRequestId) {
      return;
    }

    const matchingRequest = requests.find((request) => request.id === originalRequestId);

    if (matchingRequest) {
      setSelectedOption(matchingRequest);
    }
  }, [originalRequestId, requests, selectedOption?.id]);

  const options =
    selectedOption && !requests.some((request) => request.id === selectedOption.id)
      ? [selectedOption, ...requests]
      : requests;

  const label = t({
    id: 'defect-card.exceptional-coverage.original-request.label',
    message: 'Original request',
  });

  const handleChange = (option: WorkshopWarrantyReturnRequest | null) => {
    setSelectedOption(option);
    onChange(option);
    setSearch('');
  };

  return (
    <InputSearchSelect
      label={label}
      aria-label={label}
      placeholder={t({
        id: 'defect-card.exceptional-coverage.original-request.placeholder',
        message: 'Select the original request (optional) ...',
      })}
      value={selectedOption}
      options={options}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => getOriginalRequestOptionLabel(option)}
      formatOptionLabel={(option) => <OriginalRequestOptionLabel option={option} />}
      inputValue={search}
      onInputChange={setSearch}
      blurInputOnSelect
      filterOption={null}
      onChange={handleChange}
      isLoading={isFetching || search !== debouncedSearch}
      noOptionsMessage={() => (
        <Stack padding="0.5rem" style={{ background: 'var(--color-neutral-0)' }}>
          <Paragraph>
            {search ? (
              <Trans id="defect-card.exceptional-coverage.original-request.no-results">
                No results
              </Trans>
            ) : (
              <Trans id="defect-card.exceptional-coverage.original-request.search-hint">
                Enter a client name or request number
              </Trans>
            )}
          </Paragraph>
        </Stack>
      )}
      isDisabled={isDisabled}
    />
  );
};

const OriginalRequestOptionLabel = ({ option }: { option: WorkshopWarrantyReturnRequest }) => {
  return (
    <Stack>
      <InlineText weight="regular" color="primary">
        [{option.reference}] {!!option.client?.name && option.client.name}
      </InlineText>
    </Stack>
  );
};

const getOriginalRequestOptionLabel = (option: WorkshopWarrantyReturnRequest): string => {
  if (option.client?.name) {
    return `[${option.reference}] ${option.client.name}`;
  }

  return `[${option.reference}]`;
};
